import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { EventPayload } from "../../common/types/Event";
import { Paginated } from "../../common/types/Paginated";
import { customerUsersByCompanyId, getCoreUsers } from "../user/userAPI";
import {
  createCustomerUser,
  customerUsersAutoCompleteQuery,
  inviteExistingCustomerUser,
  inviteNewCustomerUser,
  runSetCustomerUserTemporaryPassword,
  updateCustomerUser,
  updateCustomerUserEnabledState,
  updateOrderingUserWorkLocations,
} from "./customerAPI";
import {
  CreateCustomerUser,
  InviteExistingCustomerUser,
  InviteNewCustomerUser,
  UpdateCustomerUser,
  UpdateCustomerUserEnabledState,
  UpdateOrderingUserWorkLocations,
} from "./types/commands";
import {
  CustomerUserCreated,
  ExistingCustomerUserInvited,
  NewCustomerUserInvited,
  OrderingUserWorkLocationsChanged,
} from "./types/events";
import {
  CustomerCompanyIdFilter,
  CustomerUser,
  CustomerUserInviteStatus,
  CustomerUsersAutocompleteResponse,
  CustomerUsersAutocompleteThunkFilters,
  Identifier,
} from "./types/queries";
import { getCoreUserFullName } from "../../common/utils/users";
import { CoreUser, CoreUserType } from "../user/types/entities";
import { TempPasswordUpdated } from "../../common/components/functional/tempPasswordDialog/TempPasswordDialog";

export type SliceState = {
  ids: string[];
  entities: {
    [id: string]: CustomerUser;
  };
  status: "idle" | "pending" | "succeeded" | "failed";
};

const customerUsersAdapter = createEntityAdapter<CustomerUser>();

export const fetchCustomerUsers = createAsyncThunk<
  Paginated<CustomerUser[]>,
  {},
  { state: RootState }
>(
  "customerUsers/fetchCustomerUsers",
  async (_, { requestId }) =>
    getCoreUsers({ type: CoreUserType.CUSTOMER }, { requestId }) as Promise<
      Paginated<CustomerUser[]>
    >,
);

export const fetchCustomerUsersByIds = createAsyncThunk<
  Paginated<CustomerUser[]>,
  string[],
  { state: RootState }
>(
  "customerUsers/fetchCustomerUsersById",
  async (userIds, { requestId }) =>
    getCoreUsers(
      { type: CoreUserType.CUSTOMER, userIds },
      { requestId },
    ) as Promise<Paginated<CustomerUser[]>>,
);

export const fetchCustomerUsersByCompanyId = createAsyncThunk<
  CustomerUser[],
  CustomerCompanyIdFilter,
  { state: RootState }
>(
  "customerUsers/fetchCustomerUsersByCompanyId",
  async (filters: CustomerCompanyIdFilter, { requestId }) =>
    customerUsersByCompanyId(filters, { requestId }),
);

export const createNewCustomerUser = createAsyncThunk<
  EventPayload<CustomerUserCreated>[],
  CreateCustomerUser,
  { state: RootState }
>(
  "customerUsers/createCustomerUser",
  async (customerUser: CreateCustomerUser, { requestId }) => {
    customerUser.emailAddress = customerUser.emailAddress.toLowerCase();
    return await createCustomerUser(customerUser, { requestId });
  },
);

export const inviteExistingCustomerAppUser = createAsyncThunk<
  EventPayload<ExistingCustomerUserInvited>[],
  InviteExistingCustomerUser,
  { state: RootState }
>(
  "customerUsers/inviteExistingCustomerAppUser",
  async (customerUser: InviteExistingCustomerUser, { requestId }) =>
    await inviteExistingCustomerUser(customerUser, { requestId }),
);

export const inviteNewCustomerAppUser = createAsyncThunk<
  EventPayload<NewCustomerUserInvited>[],
  InviteNewCustomerUser,
  { state: RootState }
>(
  "customerUsers/inviteNewCustomerAppUser",
  async (customerUser: InviteNewCustomerUser, { requestId }) => {
    customerUser.email = customerUser.email.toLowerCase();
    return await inviteNewCustomerUser(customerUser, { requestId });
  },
);

export const updateCustomerUserWorkLocations = createAsyncThunk<
  EventPayload<OrderingUserWorkLocationsChanged>[],
  UpdateOrderingUserWorkLocations,
  { state: RootState }
>(
  "customerUsers/updateOrderingUserWorkLocations",
  async (params: UpdateOrderingUserWorkLocations, { requestId }) =>
    await updateOrderingUserWorkLocations(params, { requestId }),
);

export const updateCustomerAppUser = createAsyncThunk<
  EventPayload<UpdateCustomerUser>,
  UpdateCustomerUser,
  { state: RootState }
>(
  "customerUsers/updateCustomerUser",
  async (params: UpdateCustomerUser, { requestId }) => {
    params.emailAddress = params.emailAddress.toLowerCase();
    return await updateCustomerUser(params, { requestId });
  },
);

export const updateCustomerAppUserEnabledState = createAsyncThunk<
  EventPayload<UpdateCustomerUserEnabledState>,
  UpdateCustomerUserEnabledState,
  { state: RootState }
>(
  "customerUsers/updateCustomerUserEnabledState",
  async (params: UpdateCustomerUserEnabledState, { requestId }) =>
    await updateCustomerUserEnabledState(params, { requestId }),
);

export const setCustomerUserTemporaryPassword = createAsyncThunk<
  TempPasswordUpdated,
  string,
  { state: RootState }
>(
  "customerUsers/setCustomerUserTemporaryPassword",
  async (customerUserId: string): Promise<TempPasswordUpdated> => {
    const response = await runSetCustomerUserTemporaryPassword(customerUserId);
    return response.data;
  },
);

export const customerUsersAutocomplete = createAsyncThunk<
  CustomerUsersAutocompleteResponse[],
  CustomerUsersAutocompleteThunkFilters,
  { state: RootState }
>(
  "customerUsers/customerUsersAutocomplete",
  async (filters: CustomerUsersAutocompleteThunkFilters, { requestId }) => {
    const { onlyActive, search, withCompanyName } = filters;
    const allCustomerUsers = await customerUsersAutoCompleteQuery(
      {
        search,
        ...(withCompanyName ? { withCompanyName: "true" as const } : {}),
      },
      {
        requestId,
      },
    );

    if (!onlyActive) {
      return allCustomerUsers;
    }

    return allCustomerUsers.filter(
      (customerUser) =>
        customerUser.registered === true && customerUser?.enabled !== false,
    );
  },
);

export const customerUsersSlice = createSlice({
  name: "customerUsers",
  initialState: customerUsersAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchCustomerUsers.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchCustomerUsers.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchCustomerUsers.fulfilled, (state, action) => {
      customerUsersAdapter.setAll(state, action.payload.data);
      state.status = "succeeded";
    });
    builder.addCase(fetchCustomerUsersByIds.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchCustomerUsersByIds.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchCustomerUsersByIds.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchCustomerUsersByCompanyId.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchCustomerUsersByCompanyId.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      fetchCustomerUsersByCompanyId.fulfilled,
      (state, action) => {
        customerUsersAdapter.setAll(state, action.payload);
        state.status = "succeeded";
      },
    );
    builder.addCase(createNewCustomerUser.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(createNewCustomerUser.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(createNewCustomerUser.fulfilled, (state, action) => {
      if (action.payload) {
        action.payload.forEach(({ name: eventName, version, payload, id }) => {
          switch (eventName) {
            case "custUsrCreated":
              const customerUser: CustomerUser = {
                id,
                type: CoreUserType.CUSTOMER,
                account: {
                  version,
                  firstName: payload.firstName,
                  lastName: payload.lastName,
                  phone: payload.phone,
                  title: payload.title,
                  emailAddress: payload.email,
                  customerUserCompanyId: payload.companyId,
                  customerUserInviteStatus: payload.inviteStatus,
                  enabled: true,
                },
              };
              customerUsersAdapter.addOne(state, customerUser);
              break;
            case "custUsrPrivilidgesChanged":
              const existingCustomerUser = state.entities[id];
              if (existingCustomerUser) {
                // Update version and authScopes
                const updatedCustomerUser = {
                  ...existingCustomerUser,
                  account: {
                    ...existingCustomerUser.account,
                    version: version,
                    authScopes: payload.authScopes,
                  },
                };

                customerUsersAdapter.updateOne(state, {
                  id,
                  changes: updatedCustomerUser,
                });
              }
              break;
            default:
              break;
          }
        });
      }
      state.status = "succeeded";
    });
    builder.addCase(updateCustomerAppUser.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateCustomerAppUser.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateCustomerAppUser.fulfilled, (state, action) => {
      if (action.payload) {
        const { id } = action.payload;
        const customerUser = state.entities[id];

        const updatedCustomerUser = {
          id,
          account: {
            ...customerUser.account,
            ...action.payload.payload,
            version: action.payload.version,
          },
        };

        customerUsersAdapter.updateOne(state, {
          id,
          changes: updatedCustomerUser,
        });
      }
      state.status = "succeeded";
    });
    builder.addCase(inviteExistingCustomerAppUser.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(inviteExistingCustomerAppUser.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      inviteExistingCustomerAppUser.fulfilled,
      (state, action) => {
        action.payload.forEach(({ name: eventName, version, payload, id }) => {
          const customerUser = state.entities[id];
          switch (eventName) {
            case "custUsrEnabled":
              const custUsrEnabledChanges = {
                account: {
                  ...customerUser.account,
                  version,
                  enabled: true,
                },
              };

              customerUsersAdapter.updateOne(state, {
                id,
                changes: {
                  ...custUsrEnabledChanges,
                },
              });
              break;
            case "custUsrInvited":
              const changes = {
                account: {
                  ...customerUser.account,
                  version,
                  customerUserInviteStatus:
                    CustomerUserInviteStatus.INVITE_PENDING,
                },
              };

              customerUsersAdapter.updateOne(state, {
                id,
                changes,
              });
              break;
            default:
              break;
          }
        });

        state.status = "succeeded";
      },
    );
    builder.addCase(inviteNewCustomerAppUser.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(inviteNewCustomerAppUser.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(inviteNewCustomerAppUser.fulfilled, (state, action) => {
      action.payload.forEach(({ name: eventName, version, payload, id }) => {
        const {
          firstName,
          lastName,
          email,
          inviteStatus,
          title,
          companyId,
          phone,
        } = payload;

        switch (eventName) {
          case "custUsrCreated":
            const newCustomerUser: CustomerUser = {
              id,
              type: CoreUserType.CUSTOMER,
              account: {
                version,
                firstName,
                lastName,
                title,
                phone,
                emailAddress: email,
                customerUserInviteStatus: inviteStatus,
                customerUserCompanyId: companyId,
                enabled: true,
              },
            };

            customerUsersAdapter.addOne(state, newCustomerUser);
            break;
          case "custUsrInvited":
            // custUsrCreated event has to be guaranteed to be first event/before custUsrInvited
            const customerUser = state.entities[id];

            const changes = {
              account: {
                ...customerUser.account,
                version,
                customerUserInviteStatus:
                  CustomerUserInviteStatus.INVITE_PENDING,
              },
            };

            customerUsersAdapter.updateOne(state, {
              id,
              changes,
            });
            break;
          default:
            // only update version
            customerUsersAdapter.updateOne(state, {
              id,
              changes: {
                account: {
                  ...state.entities[id].account,
                  version,
                },
              },
            });
        }
      });
      state.status = "succeeded";
    });
    builder.addCase(updateCustomerUserWorkLocations.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateCustomerUserWorkLocations.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      updateCustomerUserWorkLocations.fulfilled,
      (state, action) => {
        const { addTo, removeFrom, customerUserId } = action.meta.arg;

        const updateUserWorklocations = (
          id: string,
          added: string[],
          removed: string[],
        ) => {
          const customerUser = state.entities[id];

          const customerUserWorkLocationIds =
            customerUser?.account?.customerUserWorkLocationIds || [];

          const addedIds = [...customerUserWorkLocationIds, ...added];

          if (!customerUserWorkLocationIds.length) {
            return customerUsersAdapter.updateOne(state, {
              id,
              changes: {
                account: {
                  ...customerUser.account,
                  customerUserWorkLocationIds: addedIds,
                },
              },
            });
          }

          const filterRemovedIds = addedIds?.filter(
            (id) => !removed.includes(id),
          );

          customerUsersAdapter.updateOne(state, {
            id,
            changes: {
              account: {
                ...customerUser.account,
                customerUserWorkLocationIds: filterRemovedIds,
              },
            },
          });
        };

        updateUserWorklocations(customerUserId, addTo, removeFrom);
        state.status = "succeeded";
      },
    );
    builder.addCase(updateCustomerAppUserEnabledState.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateCustomerAppUserEnabledState.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      updateCustomerAppUserEnabledState.fulfilled,
      (state, action) => {
        const { id, version } = action.payload;
        const { enabled } = action.meta.arg;

        const customerUser = state.entities[id];

        const updatedCustomerUser = {
          ...customerUser,
          account: {
            ...customerUser.account,
            version,
            enabled,
          },
        };

        customerUsersAdapter.updateOne(state, {
          id,
          changes: updatedCustomerUser,
        });

        state.status = "succeeded";
      },
    );
  },
});

export const selectCustomerUsersByCompanyId = (customerCompanyId: string) =>
  createSelector(
    (state: RootState) => state.customerUsers.entities,
    (entities) =>
      Object.values(entities).filter(
        (customerUser: CustomerUser) =>
          customerUser.account.customerUserCompanyId === customerCompanyId,
      ),
  );

export const selectCustomerUsersByIds = (ids?: Identifier[]) =>
  createSelector(
    (state: RootState) => state.customerUsers.entities,
    (entities) => {
      if (!ids) return [];

      const stringIds = ids.map(({ id }) => id);

      return Object.values(entities).filter((customerUser: CustomerUser) =>
        stringIds.includes(customerUser.id),
      );
    },
  );

const formatCustomerUser = (customerUser: CoreUser) => ({
  id: customerUser.id,
  name: getCoreUserFullName(customerUser),
});

export const selectFormattedCustomerUserByIdOrNull = (id?: string) =>
  createSelector(
    (state: RootState) => state.customerUsers.entities,
    (entities) => {
      if (!id) return null;
      const customerUser = Object.values(entities).find(
        (entity) => entity.id === id,
      );

      if (customerUser) {
        return formatCustomerUser(customerUser);
      }

      return null;
    },
  );

export const selectAllFormattedCustomerUsers = () =>
  createSelector(
    (state: RootState) => state.customerUsers.entities,
    (entities) =>
      Object.values(entities).map((customerUser) =>
        formatCustomerUser(customerUser),
      ),
  );

export const {
  selectAll: selectAllCustomerUsers,
  selectById: selectCustomerUserById,
} = customerUsersAdapter.getSelectors(
  (state: RootState) => state.customerUsers,
);

export const customerUsersReducer = customerUsersSlice.reducer;
